import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import TrustTierBadge from "@/components/TrustTierBadge";
import FounderPill from "@/components/FounderPill";
import AdminAvatar from "@/components/AdminAvatar";
import FollowButton from "@/components/FollowButton";
import FollowListModal from "@/components/FollowListModal";
import MessageSellerButton from "@/components/MessageSellerButton";
import SocialLinks from "@/components/SocialLinks";
import TrustScore from "@/components/TrustScore";
import SellerReviews from "@/components/SellerReviews";
import BeaconTrophy from "@/components/beacon/BeaconTrophy";
import BeaconBoostPill from "@/components/beacon/BeaconBoostPill";
import AdminBeaconQuickToggle from "@/components/beacon/AdminBeaconQuickToggle";
import ReportDialog from "@/components/ReportDialog";
import PostCard from "@/components/PostCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { SealCheckIcon, UserCircleIcon, UsersThreeIcon, LightningIcon, TruckIcon, FlagIcon } from "@phosphor-icons/react";

// Slug → human label. Kept inline to avoid an extra round-trip to
// /api/shipping/carriers when we only need 6 names. If a slug isn't in
// the map (e.g. a future carrier we forgot to update here), we fall
// back to a Title-Cased version of the slug so nothing breaks visually.
const CARRIER_LABELS = {
  usps: "USPS",
  ups: "UPS",
  fedex: "FedEx",
  dhl: "DHL",
  lasership: "LaserShip",
  other: "Other",
};

function ShipsWithRow({ carriers }) {
  if (!carriers || carriers.length === 0) return null;
  return (
    <div
      className="mt-6 border border-zinc-200 bg-white rounded-xl px-5 py-4 flex flex-wrap items-center gap-3"
      data-testid="seller-ships-with"
    >
      <div className="flex items-center gap-2 text-zinc-700">
        <TruckIcon size={16} className="text-[#1D78AB]" />
        <span className="overline text-zinc-500">Ships with</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {carriers.map((slug) => (
          <span
            key={slug}
            className="inline-flex items-center px-3 py-1 rounded-full bg-[#1D78AB]/10 text-[#1D78AB] text-xs font-bold tracking-wide"
            data-testid={`ships-with-${slug}`}
          >
            {CARRIER_LABELS[slug] || slug.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function SellerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [seller, setSeller] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followModal, setFollowModal] = useState(null); // "followers" | "following" | null
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, l] = await Promise.all([
          api.get(`/sellers/${id}`),
          api.get("/listings", { params: { seller_id: id, limit: 24 } }),
        ]);
        setSeller(s.data);
        setListings(l.data.items);
      } catch {}
      setLoading(false);
    })();
  }, [id]);

  if (loading)
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-6xl mx-auto p-10 text-zinc-500">Loading seller…</div>
      </div>
    );
  if (!seller) return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto p-10 text-center">
        <p className="font-display font-bold text-2xl">Seller not found.</p>
        <Link to="/browse" className="text-sm text-[#1D78AB] mt-2 inline-block">
          ← Back to browse
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`seller-profile-${seller.user_id}`}>
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-10">
        <p className="overline text-zinc-500">SELLER PROFILE</p>

        {/* Cover banner — falls back to a Bidorah-themed gradient so
            profiles never feel empty. Stretched edge-to-edge on the
            inner column for a Twitter/Instagram-style header. */}
        <div
          className="mt-4 relative h-44 sm:h-56 rounded-t-2xl overflow-hidden border border-b-0 border-zinc-200"
          data-testid="profile-cover-banner"
        >
          {seller.cover_image ? (
            <img
              src={seller.cover_image}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#1D78AB] via-[#0F3C57] to-zinc-900">
              <div
                className="absolute inset-0 opacity-20 mix-blend-overlay"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 30% 20%, white 0%, transparent 30%), radial-gradient(circle at 80% 60%, white 0%, transparent 25%)",
                }}
              />
            </div>
          )}
        </div>

        <div className="border border-t-0 border-zinc-200 bg-white p-6 rounded-b-2xl flex flex-wrap items-center gap-6 relative">
          {/* Avatar overlaps the banner */}
          <div className="h-24 w-24 -mt-16 sm:-mt-20 flex-shrink-0">
            {seller.avatar_sparkle ? (
              // VIP/admin treatment — rotating sparkle ring + shimmer.
              // The AdminAvatar wrapper already paints its own circular
              // background, so we skip the ring + white shadow ring
              // here to avoid a double-border on the image.
              <AdminAvatar
                src={seller.picture || null}
                alt={seller.name}
                size={88}
                className="block"
              />
            ) : (
              <div className="h-full w-full bg-zinc-100 grid place-items-center rounded-full overflow-hidden ring-4 ring-white shadow-md">
                {seller.picture ? (
                  <img src={seller.picture} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserCircleIcon size={64} className="text-zinc-400" />
                )}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display font-black text-3xl tracking-tighter inline-flex items-center gap-2">
                {seller.name}
                <VerifiedBadge visible={seller.verified_badge} size={22} />
              </h1>
              <FounderPill is_founder={seller.is_founder} testid={`founder-pill-${seller.user_id}`} />
              <TrustTierBadge tier={seller.trust_tier} size="lg" />
              <span
                className={`px-2 py-1 text-[10px] font-bold tracking-widest uppercase ${
                  seller.segment === "wholesale" ? "bg-amber-500 text-white" : "bg-[#1D78AB] text-white"
                }`}
              >
                {seller.segment}
              </span>
              <BeaconBoostPill userId={seller.user_id} size="lg" />
              <AdminBeaconQuickToggle
                sellerId={seller.user_id}
                allowed={seller.beacon_allowed}
                onChange={(next) =>
                  setSeller((s) => (s ? { ...s, beacon_allowed: next } : s))
                }
              />
            </div>
            <BeaconTrophy userId={seller.user_id} />
            <div className="flex items-center gap-3 mt-2 text-sm text-zinc-600 flex-wrap">
              <span>
                Member since{" "}
                {seller.member_since
                  ? new Date(seller.member_since).toLocaleDateString(undefined, {
                      month: "long",
                      year: "numeric",
                    })
                  : "—"}
              </span>
              <span className="text-zinc-300">·</span>
              <button
                type="button"
                onClick={() => setFollowModal("followers")}
                className="flex items-center gap-1 hover:text-[#1D78AB] hover:underline transition-colors"
                data-testid="open-followers-modal"
              >
                <UsersThreeIcon size={14} />
                <span className="font-semibold text-zinc-900 tabular-nums">
                  {seller.follower_count || 0}
                </span>{" "}
                follower{seller.follower_count === 1 ? "" : "s"}
              </button>
              <span className="text-zinc-300">·</span>
              <button
                type="button"
                onClick={() => setFollowModal("following")}
                className="hover:text-[#1D78AB] hover:underline transition-colors"
                data-testid="open-following-modal"
              >
                <span className="font-semibold text-zinc-900 tabular-nums">
                  {seller.following_count || 0}
                </span>{" "}
                following
              </button>
            </div>
            {seller.bio && (
              <p className="mt-3 text-sm text-zinc-700 max-w-2xl whitespace-pre-wrap">
                {seller.bio}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <FollowButton
                sellerId={seller.user_id}
                initialFollowing={seller.is_following}
                initialCount={seller.follower_count}
                onChange={(d) =>
                  setSeller((prev) =>
                    prev
                      ? {
                          ...prev,
                          is_following: d.is_following,
                          follower_count: d.follower_count,
                        }
                      : prev
                  )
                }
              />
              <MessageSellerButton sellerId={seller.user_id} sellerName={seller.name} />
              <SocialLinks links={seller.social_links} />
              {user && user.user_id !== seller.user_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReportOpen(true)}
                  className="text-zinc-600 border-zinc-300 hover:text-amber-700 hover:border-amber-400 hover:bg-amber-50"
                  data-testid="seller-report-btn"
                >
                  <FlagIcon size={14} className="mr-1" /> Report
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 min-w-[220px]">
            {seller.is_seller ? (
              <>
                <Stat label="Sales" value={seller.total_sales_count} />
                <Stat label="Listings" value={seller.active_listings} />
              </>
            ) : null}
          </div>
        </div>

        {seller.is_seller && (
          <>
            <div className="mt-6">
              <TrustScore sellerId={seller.user_id} />
            </div>
            <ShipsWithRow carriers={seller.shipping_carriers} />
          </>
        )}

        {seller.is_seller && (
          <div className="mt-10">
            <p className="overline text-zinc-500 mb-3">POSTS</p>
            <SellerPostsList sellerId={seller.user_id} currentUserId={user?.user_id} />
          </div>
        )}

        {seller.is_seller && (
          <div className="mt-10">
            <p className="overline text-zinc-500 mb-3">ACTIVE LISTINGS</p>
            {listings.length === 0 ? (
              <div className="border border-zinc-200 p-12 text-center bg-white">
                <p className="font-display font-bold text-xl">No active listings.</p>
                <p className="text-sm text-zinc-600 mt-1">Check back later for new drops.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {listings.map((l) => (
                  <ProductCard key={l.id} listing={l} />
                ))}
              </div>
            )}
          </div>
        )}

        {seller.is_seller && (
          <div className="mt-10">
            <p className="overline text-zinc-500 mb-3">REVIEWS</p>
            <SellerReviews sellerId={seller.user_id} />
          </div>
        )}

        {seller.is_seller && (
          <div className="mt-10 border border-zinc-200 bg-zinc-50 p-6 text-xs text-zinc-500 leading-relaxed">
            <p className="overline mb-1">TIERS</p>
            Bidorah assigns a public trust tier based on completed sales: <b>NEW</b> (0–9),{" "}
            <b>RISING</b> (10–99), <b>TRUSTED</b> (100–499), <b>INSTANT</b> (500+). At the INSTANT tier,
            payouts release immediately on payment.
          </div>
        )}

        {!seller.is_seller && (
          <div className="mt-10 border border-zinc-200 bg-zinc-50 p-8 text-center">
            <p className="font-display font-bold text-lg text-zinc-950">
              @{seller.name} is a buyer on Bidorah.
            </p>
            <p className="text-sm text-zinc-600 mt-1">
              Follow them to get a heads-up the moment they start selling.
            </p>
          </div>
        )}
      </main>
      <FollowListModal
        open={followModal !== null}
        onOpenChange={(o) => !o && setFollowModal(null)}
        userId={seller.user_id}
        mode={followModal}
        ownerHandle={seller.name}
      />
      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        targetType="user"
        targetId={seller.user_id}
        targetLabel={`@${seller.username || seller.name}`}
      />
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-200 bg-white p-3 text-center">
      <p className="overline text-zinc-500 text-[10px]">{label}</p>
      <p className="font-display font-black text-xl mt-0.5">{value ?? 0}</p>
    </div>
  );
}

function SellerPostsList({ sellerId, currentUserId }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/sellers/${sellerId}/posts?limit=20`);
        if (!cancelled) setPosts(res.data.items || []);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellerId]);

  if (loading) {
    return <div className="text-sm text-zinc-500 py-4">Loading posts…</div>;
  }
  if (posts.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 rounded-2xl p-6 text-center bg-zinc-50">
        <p className="text-sm text-zinc-600">No posts yet.</p>
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="seller-posts-list">
      {posts.map((p) => (
        <PostCard
          key={p.id}
          post={p}
          currentUserId={currentUserId}
          onDeleted={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))}
        />
      ))}
    </div>
  );
}
